import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { APP_LOCALES, locales } from 'locale/locale.config';

import { useLocale } from 'lib/localeProvider';

/**
 * A bag of necessary HTML5 and metadata tags for a page.
 *
 * @see
 * [The Open Graph protocol](https://ogp.me/)
 *
 * @param {object} props
 */
function PageHead({
  children,
  canonicalLocale = APP_LOCALES.UK,
  title,
  description,
  ogDescription,
  ogImageUrl,
  ogImageType,
  ogImageHeight,
  ogImageWidth,
  ogTitle,
  ogType = 'website',
  ogVideoUrl,
  pageLocales,
  robots,
}) {
  const { localeLang } = useLocale();
  const { pathname, query } = useRouter();

  const pathWithoutLocale = pathname
    .replace('/[locale]', '')
    .replace(/\[(.+?)\]/g, (_, key) => query[key]);

  const canonicalUrl = `${process.env.BRAND_DOMAIN}/${canonicalLocale}${pathWithoutLocale}`;

  return (
    <Head>
      <title>{title ?? process.env.BRAND_NAME}</title>
      {description && <meta name="description" content={description} />}

      {robots && <meta name="robots" content={robots} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Tell Google about each localized version of a page */}
      {pageLocales?.map(locale => {
        const href = pathWithoutLocale
          ? `${process.env.BRAND_DOMAIN}/${locale}${pathWithoutLocale}`
          : `${process.env.BRAND_DOMAIN}/${locale}`;

        const hreflang = locales[locale].lang;

        return (
          <link href={href} hrefLang={hreflang} key={locale} rel="alternate" />
        );
      })}

      {/* Open Graph markdown */}
      {(ogTitle ?? title) && (
        <>
          <meta property="og:title" content={ogTitle ?? title} />
          <meta property="twitter:title" content={ogTitle ?? title} />
        </>
      )}

      {ogDescription && (
        <meta
          property="og:description"
          content={ogDescription ?? description}
        />
      )}

      {ogImageUrl ? (
        <>
          <meta property="og:image" content={ogImageUrl} />
          {ogImageType && (
            <meta property="og:image:type" content={ogImageType} />
          )}
          {ogImageHeight && (
            <meta property="og:image:height" content={ogImageHeight} />
          )}
          {ogImageWidth && (
            <meta property="og:image:width" content={ogImageWidth} />
          )}
        </>
      ) : (
        <>
          <meta
            property="og:image"
            content={`${process.env.BRAND_DOMAIN}/media/og-image-${process.env.BRAND_ID}-min.jpg`}
          />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:width" content="1200" />
        </>
      )}

      {ogVideoUrl && <meta property="og:video" content={ogVideoUrl} />}

      <link rel="og:url" href={canonicalUrl} />

      <meta property="article:author" content={process.env.FACEBOOK_PAGE_URL} />

      <meta
        property="article:publisher"
        content={process.env.FACEBOOK_PAGE_URL}
      />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={process.env.BRAND_NAME} />

      {/* Use only for content not written in American English (en-US).
      Facebook assumes content without this tag is written in this language. */}
      {localeLang && <meta property="og:locale" content={localeLang} />}

      {children}
    </Head>
  );
}

PageHead.propTypes = {
  children: PropTypes.node,
  /** The page's canonical locale */
  canonicalLocale: PropTypes.string,
  /**
   * A short and accurate summary of the content of the page.
   * Usually shown within search engine's results.
   */
  description: PropTypes.string,
  /** The title of the content without any branding (such as the site name) */
  ogTitle: PropTypes.string,
  /** The media type of the page */
  ogType: PropTypes.string,
  /** A brief description of the content, usually between 2 and 4 sentences.
   * Usually will displayed below the title of the post.
   */
  ogDescription: PropTypes.string,
  /** The canonical URL for the page. It helps consolidate all connected data,
   * such as likes, across all the duplicate URLs posted.
   */
  url: PropTypes.string,
  /** The URL of the image that appears when someone shares the content. */
  ogImageUrl: PropTypes.string,
  /** MIME type of the image */
  ogImageType: PropTypes.oneOf(['image/jpeg', 'image/gif', 'image/png']),
  /** Height of image in pixels */
  ogImageHeight: PropTypes.number,
  /** Width of image in pixels */
  ogImageWidth: PropTypes.number,
  /** The URL of the video that appears when someone shares the content */
  ogVideoUrl: PropTypes.string,
  /**
   * Tell Google about each localized version of a page
   * https://developers.google.com/search/docs/advanced/crawling/localized-versions
   */
  pageLocales: PropTypes.array,
  /** Provide crawlers instructions for how to crawl or index the content  */
  robots: PropTypes.string,
  /**
   * Defines the document's title that is shown in a browser's title bar or a
   * page's tab.
   */
  title: PropTypes.string,
};

export default PageHead;
